import React, { useState } from 'react';
import DrMaxReserveMedicamentsList from './DrMaxReserveMedicamentsList';
import useTranslation from '../../../../Hooks/useTranslation';
import {
	useCreateDrMaxPrescriptionReservationMutation,
	useLazyGetDrMaxPrescriptionMedicamentsQuery,
} from '../../../../graphql/graphql';
import { useEffectAsync } from '../../helpers';
import { Medicament } from './DrMaxReserveMedicamentsListItem';
import Loading from '../../../BaseModule/Components/Loading';
// @ts-ignore
import styles from './DrMaxReserveMedicaments.module.scss';
import { Pharmacy } from '../DrMaxPharmacyList/DrMaxPharmacyListItem';
import { useAppContext } from '../../../../Services/AppContext';
import useNotification from '../../../../Hooks/useNotification';
import PillButton from '../../../BaseModule/Components/Button/PillButton/PillButton';
import DrMaxPill from '../../../BaseModule/Components/Pills/DrMaxPill';
import DrMaxPharmacy from '../DrMaxPharmacy/DrMaxPharmacy';
import FormControl from '../../../BaseModule/Components/Form/FormControl';
import Checkbox from '../../../BaseModule/Components/Checkbox/Checkbox';
import DrMaxPharmacyWorkingHours from '../DrMaxPharmacyWorkingHours/DrMaxPharmacyWorkingHours';
import NetteApiClient from '../../../../Services/NetteApiClient';

interface Props {
	prescriptionId: string;
	pharmacy: Pharmacy;
	onSuccess?: () => void;
	onPharmacyChange: () => void;
}

const DrMaxReserveMedicaments: React.FC<Props> = ({
	prescriptionId,
	pharmacy,
	onSuccess,
	onPharmacyChange,
}) => {
	const [medicaments, setMedicaments] = useState<Medicament[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isConditionChecked, setIsConditionChecked] = useState(false);
	const { t } = useTranslation();

	// @ts-ignore
	const { userData, redirectPrescriptionDetail } = useAppContext();
	const { showNotificationAlert } = useNotification();
	const [getDrMaxPrescriptionMedicaments] =
		useLazyGetDrMaxPrescriptionMedicamentsQuery();
	const [createDrMaxPrescriptionReservation] =
		useCreateDrMaxPrescriptionReservationMutation();

	useEffectAsync(async () => {
		setIsLoading(true);

		const { drmaxPrescriptionMedicaments } =
			await getDrMaxPrescriptionMedicaments({
				prescriptionId,
				drmaxPickupPlaceId: pharmacy.id,
			}).unwrap();

		const items: Medicament[] = [];

		for (const medicament of drmaxPrescriptionMedicaments?.data || []) {
			items.push({
				name: medicament?.name ?? '',
				formattedApproximatePrice: medicament?.formattedApproximatePrice ?? '',
				inStock: medicament?.inStock ?? false,
			});
		}
		setMedicaments(items);
		setIsLoading(false);
	}, []);

	const handleConfirmReservation = async () => {
		if (isConditionChecked) {
			try {
				const result = await createDrMaxPrescriptionReservation({
					input: {
						prescriptionId,
						drmaxPickupPlaceId: pharmacy.id,
						userId: userData?.id,
					},
				}).unwrap();

				await NetteApiClient.get(
					redirectPrescriptionDetail.replace('__ID__', prescriptionId),
				);
				onSuccess && onSuccess();
			} catch (e: any) {
				if (e.response.data.displayableError) {
					showNotificationAlert({
						content: e.response.data.message,
					});
				}
			}
		} else {
			showNotificationAlert({
				content: t('component.drmax.youMustAgreeWithReservationConditions'),
			});
		}
	};

	return (
		<div className={styles.wrapper}>
			{isLoading && <Loading />}
			<DrMaxPharmacy item={pharmacy} onChange={onPharmacyChange} />
			<div className={'mt-4'}>
				<DrMaxReserveMedicamentsList items={medicaments} />
			</div>
			<div className='mt-4'>
				<Checkbox
					value={isConditionChecked}
					onChange={() => setIsConditionChecked(!isConditionChecked)}
				>
					<div>
						<div
							dangerouslySetInnerHTML={{
								__html: t('component.drmax.iAgreeWith') ?? '',
							}}
						/>
					</div>
				</Checkbox>
			</div>
			<div className={'mt-3'}>
				<PillButton
					text='component.drmax.confirmReservation'
					PillComponent={<DrMaxPill viewBox='0 0 60 22' />}
					onClick={handleConfirmReservation}
				/>
			</div>
			{pharmacy.openingHours && (
				<div className={'mt-3'}>
					<DrMaxPharmacyWorkingHours
						openingHours={pharmacy.openingHours ?? []}
					/>
				</div>
			)}
		</div>
	);
};

export default DrMaxReserveMedicaments;
